import { useState, useCallback, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/api/apiClient';

/**
 * Hook global pour gérer les likes de produits (Coups de cœur / Matches)
 * Synchronisé avec React Query pour une mise à jour instantanée et partagée
 * entre toutes les cartes (Recherche, Collections, Matches, Marque, etc.).
 */
export function useLikedProducts() {
    const queryClient = useQueryClient();

    // Récupération de toutes les interactions utilisateur
    const { data: interactions = [], isLoading } = useQuery({
        queryKey: ['interactions'],
        queryFn: async () => {
            try {
                await apiClient.auth.me();
            } catch (e) {
                // Mode invité
            }
            return apiClient.entities.UserInteraction.list();
        },
        staleTime: 1000 * 30, // 30s
    });

    // Set des IDs de produits likés (action === 'swipe_right' ou 'like')
    const likedProductIds = useMemo(() => {
        const set = new Set();
        if (Array.isArray(interactions)) {
            interactions.forEach((i) => {
                if (i && ['swipe_right', 'like'].includes(i.action) && i.product_id) {
                    set.add(String(i.product_id));
                }
            });
        }
        return set;
    }, [interactions]);

    // Mutation pour basculer le like (Like / Unlike)
    const toggleLikeMutation = useMutation({
        mutationFn: async ({ product, currentlyLiked }) => {
            const productIdStr = String(product.id);

            // 1. Nettoyer les interactions existantes pour ce produit
            const existingForProduct = Array.isArray(interactions)
                ? interactions.filter((i) => String(i.product_id) === productIdStr)
                : [];

            if (existingForProduct.length > 0) {
                await Promise.all(
                    existingForProduct.map((i) =>
                        apiClient.entities.UserInteraction.delete(i.id).catch(() => null)
                    )
                );
            }

            // 2. Créer la nouvelle interaction
            if (currentlyLiked) {
                // Déliker -> swipe_left (refus / retrait)
                await apiClient.entities.UserInteraction.create({
                    product_id: product.id,
                    action: 'swipe_left',
                    context: 'card_quick_toggle',
                    product,
                });
                return { action: 'unliked', productId: productIdStr };
            } else {
                // Liker -> swipe_right
                await apiClient.entities.UserInteraction.create({
                    product_id: product.id,
                    action: 'swipe_right',
                    context: 'card_quick_toggle',
                    product,
                });
                return { action: 'liked', productId: productIdStr };
            }
        },
        onMutate: async ({ product, currentlyLiked }) => {
            // Annuler les requêtes en cours pour éviter d'écraser l'état optimiste
            await queryClient.cancelQueries({ queryKey: ['interactions'] });
            await queryClient.cancelQueries({ queryKey: ['interactions', 'likes'] });

            const previousInteractions = queryClient.getQueryData(['interactions']) || [];
            const productIdStr = String(product.id);

            let nextInteractions;
            if (currentlyLiked) {
                // Retirer les likes de la liste
                nextInteractions = previousInteractions.filter(
                    (i) => !(String(i.product_id) === productIdStr && ['swipe_right', 'like'].includes(i.action))
                );
            } else {
                // Ajouter le like optimiste
                const optimisticInteraction = {
                    id: `opt_${Date.now()}_${productIdStr}`,
                    product_id: productIdStr,
                    action: 'swipe_right',
                    created_at: new Date().toISOString(),
                    product,
                };
                nextInteractions = [optimisticInteraction, ...previousInteractions];
            }

            queryClient.setQueryData(['interactions'], nextInteractions);
            queryClient.setQueryData(['interactions', 'likes'], nextInteractions.filter(i => ['swipe_right', 'like'].includes(i.action)));

            return { previousInteractions };
        },
        onError: (err, variables, context) => {
            if (context?.previousInteractions) {
                queryClient.setQueryData(['interactions'], context.previousInteractions);
            }
        },
        onSettled: (data, error, { product }) => {
            queryClient.invalidateQueries({ queryKey: ['interactions'] });
            queryClient.invalidateQueries({ queryKey: ['interactions', 'likes'] });
            if (product?.id) {
                queryClient.invalidateQueries({ queryKey: ['interactions', product.id] });
                queryClient.invalidateQueries({ queryKey: ['interactions', String(product.id)] });
            }
        },
    });

    const isLiked = useCallback(
        (productId) => {
            if (!productId) return false;
            return likedProductIds.has(String(productId));
        },
        [likedProductIds]
    );

    const toggleLike = useCallback(
        (product, e) => {
            if (e) {
                if (typeof e.preventDefault === 'function') e.preventDefault();
                if (typeof e.stopPropagation === 'function') e.stopPropagation();
            }
            if (!product?.id) return Promise.resolve();

            const currentlyLiked = likedProductIds.has(String(product.id));
            return toggleLikeMutation.mutateAsync({ product, currentlyLiked });
        },
        [likedProductIds, toggleLikeMutation]
    );

    return {
        interactions,
        likedProductIds,
        isLiked,
        toggleLike,
        isLoading,
        isToggling: toggleLikeMutation.isPending,
    };
}
